import React, { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Mail, Briefcase } from "lucide-react";

export default function AdminInbox() {
  const [casting, setCasting] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    api.get("/admin/casting").then((r) => setCasting(r.data)).catch(() => {});
    api.get("/admin/contact").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold">Enquiries</h1>
      <Tabs defaultValue="casting" className="mt-5">
        <TabsList>
          <TabsTrigger value="casting" data-testid="tab-casting"><Briefcase size={15} className="mr-2" /> Casting ({casting.length})</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages"><Mail size={15} className="mr-2" /> Messages ({messages.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="casting" className="mt-4 space-y-3">
          {casting.length === 0 && <p className="text-sm text-zinc-400">No casting requests yet.</p>}
          {casting.map((c) => (
            <div key={c.id} data-testid={`casting-${c.id}`} className="rounded-xl border border-zinc-200 bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><span className="font-heading font-bold">{c.name}</span>{c.company && <span className="text-zinc-500"> · {c.company}</span>}</div>
                <span className="text-xs text-zinc-400">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-1 text-sm text-zinc-600">{c.email}{c.phone && ` · ${c.phone}`}</div>
              <div className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                {c.company && <Info l="Company" v={c.company} />}
                {c.role && <Info l="Role" v={c.role} />}
                {c.project_type && <Info l="Type" v={c.project_type} />}
                {c.languages && <Info l="Language(s)" v={c.languages} />}
                {c.country_accent && <Info l="Country / accent" v={c.country_accent} />}
                {c.gender_pref && <Info l="Gender" v={c.gender_pref} />}
                {c.voice_age && <Info l="Voice age" v={c.voice_age} />}
                {c.style && <Info l="Style" v={c.style} />}
                {c.child_name && <Info l="Child / talent name" v={c.child_name} />}
                {c.deadline && <Info l="Preferred delivery date" v={c.deadline} />}
                {c.script_length && <Info l="Script length" v={c.script_length === "Other" ? (c.script_length_other || "Other") : c.script_length} />}
                {c.usage_media?.length > 0 && <Info l="Usage" v={c.usage_media.join(", ")} />}
                {c.usage_territory && <Info l="Usage territory" v={c.usage_territory} />}
                {c.usage_period && <Info l="Usage duration" v={c.usage_period} />}
                <Info l="Remote direction" v={c.remote_direction ? "Yes" : "No"} />
              </div>
              {c.selected_talent_names?.length > 0 && (
                <div className="mt-3">
                  <span className="label-eyebrow text-zinc-500">Shortlisted / preferred voices</span>
                  <div className="mt-1 flex flex-wrap gap-1.5">{c.selected_talent_names.map((n) => <span key={n} className="rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-semibold text-brand-violet">{n}</span>)}</div>
                </div>
              )}
              <div className="mt-3">
                <span className="label-eyebrow text-zinc-500">Script</span>
                <div className="mt-1 text-sm text-zinc-700">
                  {c.script_file_url ? <a href={mediaUrl(c.script_file_url)} target="_blank" rel="noreferrer" className="font-semibold text-brand-violet hover:underline">{c.script_file_name || "Download script"}</a>
                    : c.script_provided_later ? "Will be provided later"
                    : c.script ? <span className="whitespace-pre-wrap">{c.script}</span>
                    : "—"}
                </div>
              </div>
              {(c.instructions || c.brief) && (
                <div className="mt-3">
                  <span className="label-eyebrow text-zinc-500">Instructions / brief</span>
                  <p className="mt-1 whitespace-pre-wrap rounded-lg bg-zinc-50 p-3 text-sm text-zinc-600">{c.instructions || c.brief}</p>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="messages" className="mt-4 space-y-3">
          {messages.length === 0 && <p className="text-sm text-zinc-400">No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} className="rounded-xl border border-zinc-200 bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-heading font-bold">{m.name}</span>
                <span className="text-xs text-zinc-400">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-zinc-600">{m.email}{m.subject && ` · ${m.subject}`}</div>
              <p className="mt-3 rounded-lg bg-zinc-50 p-3 text-sm text-zinc-600">{m.message}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Info({ l, v }) { return <div><span className="text-zinc-400">{l}: </span><span className="font-medium text-zinc-700">{v}</span></div>; }
